import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
} from "antd";
import useFetchData from "shared/hooks/useFetchData";
import "./MyComponent.css";
import Income from "./Income";
import { API_BASE_URL } from "shared/api/config";

interface WareHouseItem {
  _id: string;
  type: "frame" | "paper";
  size: string;
  quantity: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse {
  data: WareHouseItem[];
  success: string;
  message: string;
}

const typeLabels: Record<string, string> = {
  frame: "Жааз",
  paper: "Цаас",
};

const getStockColor = (quantity: number) => {
  if (quantity <= 20) return "red";
  if (quantity <= 50) return "orange";
  return "green";
};

const MyComponent: React.FC = () => {
  const { data, loading, error, refetch } = useFetchData<ApiResponse>(
    `${API_BASE_URL}/warehouse/get`
  );

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const rows = useMemo(
    () =>
      [...(data?.data || [])].sort(
        (a, b) => a.type.localeCompare(b.type) || a.size.localeCompare(b.size)
      ),
    [data]
  );

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();

    return rows.filter((item) => {
      const matchesType = selectedType ? item.type === selectedType : true;
      const matchesSearch = query
        ? [typeLabels[item.type], item.type, item.size].some((value) =>
            String(value || "").toLowerCase().includes(query)
          )
        : true;

      return matchesType && matchesSearch;
    });
  }, [rows, search, selectedType]);

  const totals = useMemo(
    () => ({
      paper: rows
        .filter((item) => item.type === "paper")
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      frame: rows
        .filter((item) => item.type === "frame")
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      lowStock: rows.filter((item) => Number(item.quantity || 0) <= 20).length,
    }),
    [rows]
  );

  if (loading) {
    return (
      <div className="centered-state">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Агуулахын дата татахад алдаа гарлаа" description={error.message} type="error" />;
  }

  return (
    <div className="warehouse-page">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Цаасны нийт үлдэгдэл" value={totals.paper} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Жаазны нийт үлдэгдэл" value={totals.frame} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Бага үлдэгдэлтэй төрөл" value={totals.lowStock} />
          </Card>
        </Col>
      </Row>

      <Card className="warehouse-toolbar">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Input.Search
              allowClear
              placeholder="Төрөл, хэмжээгээр хайх"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              allowClear
              placeholder="Төрөл сонгоно уу"
              value={selectedType}
              onChange={setSelectedType}
              options={[
                { label: "Цаас", value: "paper" },
                { label: "Жааз", value: "frame" },
              ]}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              Орлого нэмэх
            </Button>
          </Col>
        </Row>
      </Card>

      <Table<WareHouseItem>
        dataSource={filteredData}
        rowKey="_id"
        size="small"
        bordered
        scroll={{ x: 720 }}
        pagination={false}
        columns={[
          {
            title: "Төрөл",
            dataIndex: "type",
            key: "type",
            render: (type: string) => <Tag color={type === "frame" ? "purple" : "cyan"}>{typeLabels[type] || type}</Tag>,
          },
          { title: "Хэмжээ", dataIndex: "size", key: "size" },
          {
            title: "Тоо",
            dataIndex: "quantity",
            key: "quantity",
            align: "right",
            render: (quantity: number) => <Tag color={getStockColor(quantity)}>{quantity}</Tag>,
          },
          {
            title: "Шинэчилсэн",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (value: string) => new Date(value).toLocaleString(),
          },
        ]}
      />

      <Modal
        title="Орлого нэмэх"
        open={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Income
            onSuccess={() => {
              setIsModalOpen(false);
              refetch();
            }}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default MyComponent;
